import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Typography from "@mui/material/Typography";
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import Paper from '@mui/material/Paper';
import Zoom from '@mui/material/Zoom';
import Grow from '@mui/material/Grow';
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Slide from '@mui/material/Slide';
import { useMediaQuery, useTheme } from '@mui/system';
import { InView } from "react-intersection-observer";

// Import icons
import CheckIcon from '@mui/icons-material/Check';
import StarPurple500Icon from '@mui/icons-material/StarPurple500';
import ForwardOutlinedIcon from '@mui/icons-material/ForwardOutlined';
import FiberManualRecordOutlinedIcon from '@mui/icons-material/FiberManualRecordOutlined';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';

// Local imports
import './Homepage.css';
import Notification from "../Notification";

const paperStyles = {
  width: {
    xs: '100%',
    sm: '90%',
    md: '80%'
  },
  margin: '0 auto', 
  padding: '1rem', 
  backgroundColor: '#f7f7f7',
  maxWidth: '1200px',
  my: '2rem', 
}

const cardStyles = {
  height: '100%',
  backgroundColor: '#f7f7f7',
  padding: {
    xs: 1,
    sm: 2
  },
  width: {
    xs: '90%',
    sm: '80%',
    md: '90%'
  },
  margin: '0 auto',
  mb: 2,
}
const cardImgStyles = {
  height: {
    xs: '20rem',
    sm: '25rem',
    md: '20rem'
  }
}

const listStyles = {
  display: 'flex',
  alignItems: 'flex-start',
  px: {
    xs: 1,
    sm: 2
  }
}

const imgStyles = {
  height: {
    xs: '25rem',
    sm: '30rem',
    md: '20rem'
  }, 
  objectFit: 'cover', 
  borderRadius: '10px',
  maxWidth: '100%'
}

const boxStyles = {
  display: 'flex', 
  my: '2rem', 
  flexDirection: { 
    xs: 'column', 
    sm: 'row' 
  },
  alignItems: 'flex-start'
}

const imgContainerStyles = {
  fontWeight: '600', 
  textAlign: 'center',
  mb: 2,
  width: {
    xs: '100%',
    sm: '50%',
  }
}

const textContainerStyles = {
  width: {
    xs: '100%',
    sm: '90%',
    md: '80%'
  }, 
  margin: '0 auto', 
  borderRadius: '30px', 
  mt: '2rem', 
  borderLeft: '7px solid rgb(91, 59, 140)', 
  padding: '1rem', 
  backgroundColor: '#f7f7f7'
}

const Homepage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [alert, setAlert] = useState(false);
  const [alertData, setAlertData] = useState({});
  const { t, i18n } = useTranslation();
  const currentLng = i18n.language;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const videoURLVi =  "https://player.vimeo.com/video/1112365889?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479";
  const videoURLEn = "https://player.vimeo.com/video/1112365123?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479";

  useEffect(() => {
    if (location.state) {
      setAlertData({severity: location.state.severity, message: location.state.message});
      setAlert(true);

      navigate(location.pathname, { replace: true });
    }
  }, [location.state]);
  
  return (
    <div>
      {alert ? (
        <Notification severity={alertData.severity} message={alertData.message} onClose={() => setAlert(false)} />
      ) : null}
      <Fade in timeout={1000}>
        <Box sx={{ mb: 5 }}>
          {/* Hero Section */}
          <Box sx={{ 
            my: {
              xs: '1rem',
              md: '3rem'
            },
            textAlign: 'center', 
            padding: {
              xs: '1rem',
              md: '4rem'
            },
            backgroundImage: "url(/images/Facets_Background2.png)",
            backgroundSize: 'cover',
            color: '#fff',
            }}>
              <Box sx={{ 
                width: {
                  xs: '100%',
                  sm: '70%',
                }, 
                margin: '0 auto' 
                }}>
                <Typography variant={isMobile ? "h6" : "h4"} sx={{ mb: '2rem', mt: '2rem'}}>{t('homepage.title_1')} </Typography>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ mb: '2rem'}}>{t('homepage.title_2')} </Typography>
              </Box>
          </Box>
          {/* Hero Section End */}

          {/* Enroll Today Section */}
          <InView threshold={0.5} triggerOnce={true}>
            {({ inView, ref }) => (
            <Box ref={ref}>
              <Zoom in={inView} timeout={1000} >
                <Paper sx={{ 
                  width: '30rem',
                  maxWidth: '90%',
                  backgroundColor: '#f7f7f7', 
                  margin: '0 auto', 
                  padding: '2rem', 
                  borderRadius: '50px', 
                  boxShadow: '10px 10px 10px rgb(171, 167, 167)', 
                  border: '3px solid rgb(91, 59, 140)' 
                }}>
                  <Typography variant="h6" sx={{ textAlign: 'center', mb: '1rem' }}>{t('homepage.title_3')} </Typography>
                  <Box sx={{ 
                    textAlign: 'center', 
                    width: 'fit-content',
                    margin: '0 auto' ,
                    backgroundColor: 'rgb(104, 76, 150)',
                    borderRadius: '30px',
                    py: '1rem',
                    px: '2rem',
                    }}>
                    <Link to="/participate" className="enroll-link">{t('homepage.enroll_today')}</Link>
                  </Box>
                </Paper>
              </Zoom>
            </Box>
            )}
          </InView>
          {/* Enroll Today Section End */}
              
          <Box sx={{ background: 'linear-gradient(0.25turn, rgb(131, 110, 170), rgb(228, 224, 238), #836EAA)', mt: '3rem' }}>
            <Box sx={{ ...paperStyles, backgroundColor: 'inherit', pb: '3rem' }}>
              {/* Introduction Video Section */}
              <InView threshold={0.3} triggerOnce={true}>
                {({ inView, ref }) => (
                  <Box ref={ref}>
                    <Fade in={inView} timeout={1000}>
                      <Box ref={ref} sx={{width: '100%', margin: '0 auto' }}>
                         <Box sx={{ 
                            position: 'relative', 
                            pb: '56.25%', 
                            overflow: 'hidden',
                            width: '100%',
                            margin: '2rem auto 0 auto',
                            '& iframe': {
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              height: '100%',
                              border: 'none',
                              borderRadius: '10px',
                            }}}>
                            <CardMedia
                              component="iframe"
                              src={currentLng === 'vi' ? videoURLVi : videoURLEn}
                              title="HPV Vaccination Introduction Video"
                              allowFullScreen
                              />
                          </Box>
                      </Box>
                    </Fade>
                  </Box>
                )}
              </InView>
              {/* Introduction Video Section End */}

              <InView threshold={0.5} triggerOnce={true}>
                {({ inView, ref }) => (
                <Box ref={ref}>
                  <Slide direction="up" in={inView} timeout={1000} >
                    <Box sx={boxStyles}>
                      <Box sx={{ ...imgContainerStyles, mr: '2rem' }}>
                        <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 600, mb: 2 }}>{t('homepage.title_4')}</Typography>
                        <Typography variant={isMobile ? "h6" : "h5"} sx={{ textAlign: {xs: 'left', sm: 'center'}}}>{t('homepage.paragraph_1')}</Typography>
                      </Box>
                      <Box sx={{ width: { xs: '100%', sm: '50%' }, display: 'flex', justifyContent: 'center', alignItems: 'center',}}>
                         <Box component="img" src='/images/hoi-an-photographer-unsplash.webp' alt='A family poses for a picture in a park' sx={imgStyles} loading='lazy' />
                      </Box>
                    </Box>
                  </Slide>
                </Box>
                )}
              </InView>
              <InView threshold={0.3} triggerOnce={true}>
                {({ inView, ref }) => (
                  <Box ref={ref}>
                    <Slide direction="up" in={inView} timeout={1000}>
                      <Box sx={boxStyles}>
                        {isMobile ? (
                          <>
                            <Box sx={imgContainerStyles}>
                              <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 600, mb: 2 }}>{t('homepage.title_5')}</Typography>
                              <Typography variant={isMobile ? "h6" : "h5"} sx={{ textAlign: {xs: 'left', sm: 'center'}}}>{t('homepage.paragraph_2')}</Typography>
                            </Box>
                            <Box sx={{ width: { xs: '100%', sm: '50%' }, display: 'flex', justifyContent: 'center', alignItems: 'center',}}>
                              <Box component="img" src='/images/charlesdeluvio-unsplash.webp' alt='Vaccination' sx={imgStyles} loading='lazy' />
                            </Box>
                          </>
                        ) : (
                          <>
                            <Box sx={{ width: { xs: '90%', sm: '50%' }, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                              <Box component="img" src='/images/charlesdeluvio-unsplash.webp' alt='Vaccination' sx={{...imgStyles, width: '100%', mr: 4}} loading='lazy' />
                            </Box>
                            <Box sx={imgContainerStyles}>
                              <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>{t('homepage.title_5')}</Typography>
                              <Typography variant="h5">{t('homepage.paragraph_2')}</Typography>
                            </Box>
                          </>
                        )}
                      </Box>
                    </Slide>
                  </Box>
                )}
              </InView>
              <InView threshold={0.3} triggerOnce={true}>
                {({ inView, ref }) => (
                  <Box ref={ref}>
                    <Slide direction="up" in={inView} timeout={1000}>
                      <Box sx={textContainerStyles}>
                        <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ mb: '1rem', fontStyle: 'italic' }}>{t('homepage.paragraph_3')}</Typography>
                      </Box>
                    </Slide>
                  </Box>
                )}
              </InView>
              <InView threshold={0.3} triggerOnce={true}>
                {({ inView, ref }) => (
                  <Box ref={ref}>
                    <Slide direction="up" in={inView} timeout={1000}>
                      <Box ref={ref} sx={{...boxStyles, justifyContent: 'center'}}>
                        <Typography 
                          variant="h3" 
                          sx={{ 
                            mr: '4rem', 
                            width: { 
                              xs: '100%', 
                              sm: '40%' 
                            }, 
                            textAlign: 'center', 
                            display: 'flex', 
                            alignItems: 'center', 
                            fontSize: { 
                              xs: '1.5rem', 
                              sm: '2rem', 
                              md: '3rem' 
                            }, 
                            mb: 2 
                          }}>
                            {t('homepage.subtitle_1')}
                          </Typography>
                        <Box 
                          component="img" 
                          src='/images/joyce-panda-unsplash.webp' 
                          alt='Two girls sitting on swing' 
                          sx={{ 
                            width: { 
                              xs: '100%', 
                              sm: '30%' 
                            }, 
                            height: '20rem', 
                            objectFit: 'cover', 
                            borderRadius: '10px' 
                          }} 
                          loading='lazy' 
                          />
                      </Box>
                    </Slide>
                  </Box>
                  
                )}
              </InView>
              <InView threshold={0.3} triggerOnce={true}>
                {({ inView, ref }) => (
                  <Box ref={ref}>
                    <Slide direction="up" in={inView} timeout={1000}>
                      <Paper ref={ref} sx={textContainerStyles}>
                        <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ mb: '1rem' }}>{t('homepage.paragraph_4')}</Typography>
                      </Paper>
                    </Slide>
                  </Box>
                )}
              </InView>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', mt: '2rem', maxWidth: '1500px', margin: '0 auto' }}>
            <InView threshold={0.2} triggerOnce={true}>
              {({ inView, ref }) => (
                <Box ref={ref} id="eligibility">
                  <Grow in={inView} timeout={1000}>
                      <Card sx={cardStyles}>
                        <Link to={{ hash: "#eligibility" }} style={{ textDecoration: 'none' }}>
                          <CardMedia
                            sx={cardImgStyles}
                            image="/images/pexels-rdne-8298515.webp"
                            title="Grandfather and girl playing game"
                          />
                        </Link>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: '600' }}>{t('homepage.subtitle_2')}</Typography>
                          <List>
                            <ListItem sx={listStyles}><CheckIcon sx={{mr: { xs: 1, md: 2 }}} />{t('homepage.eligibility_1')}</ListItem>
                            <ListItem sx={listStyles}><CheckIcon sx={{mr: { xs: 1, md: 2 }}} />{t('homepage.eligibility_2')}</ListItem>
                            <ListItem sx={listStyles}><CheckIcon sx={{mr: { xs: 1, md: 2 }}} />{t('homepage.eligibility_3')}</ListItem>
                            <ListItem sx={listStyles}><CheckIcon sx={{mr: { xs: 1, md: 2 }}} />
                              {t('homepage.eligibility_4')} 
                              <Link to="/faq#vaccination-status" state={{ fromHomepage: true }} style={{ textDecoration: 'none', display: 'contents' }}>{t('homepage.vaccination_status_link')}</Link>
                            </ListItem>
                          </List>
                          <Typography variant="body1" sx={{ fontStyle: 'italic', mb: '1rem' }}>{t('homepage.eligibility_note')}</Typography>
                          <Typography variant="subtitle1" sx={{ mb: '1rem', fontWeight: '600' }}>{t('homepage.subtitle_3')}</Typography>
                        </CardContent>
                      </Card>
                  </Grow>
                </Box>
              )}
            </InView>
            <InView threshold={0.2} triggerOnce={true}>
              {({ inView, ref }) => (
                <Box ref={ref}>
                  <Grow in={inView} timeout={2000}>
                    <Card sx={cardStyles}>
                      <CardMedia
                        sx={cardImgStyles}
                        image="/images/vinh-thang-I-GK9YfGBwE-unsplash.jpg"
                        title="A group of people posing for a photo"
                      />
                      <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: '600' }}>{t('homepage.subtitle_4')}</Typography>
                        <List>
                          <ListItem sx={listStyles}><StarPurple500Icon sx={{mr: '1rem'}} />{t('homepage.goal_1')}</ListItem>
                          <ListItem sx={listStyles}><StarPurple500Icon sx={{mr: '1rem'}} />{t('homepage.goal_2')}</ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grow>
                </Box>
              )}
            </InView>
            <InView threshold={0.2} triggerOnce={true}>
              {({ inView, ref }) => (
                <Box ref={ref}>
                  <Grow in={inView} timeout={3000}>
                    <Card sx={cardStyles}>
                      <CardMedia
                        sx={cardImgStyles}
                        image="/images/zhen-yao-unsplash.webp"
                        title="Woman using phone at desk"
                      />
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: '600' }}>{t('homepage.subtitle_5')}</Typography>
                        <List>
                          <ListItem sx={listStyles}><ForwardOutlinedIcon sx={{mr: '1rem' }} />{t('homepage.task_1')}</ListItem>
                          <ListItem sx={{ ...listStyles, fontStyle: 'italic', pb: '0px', alignItems: 'baseline' }}><FiberManualRecordOutlinedIcon sx={{mr: '1rem', ml: '2rem', fontSize: 'small'}} />{t('homepage.subtask_1')}</ListItem>
                          <ListItem sx={{ ...listStyles, fontStyle: 'italic', pb: '0px', alignItems: 'baseline' }}><FiberManualRecordOutlinedIcon sx={{mr: '1rem', ml: '2rem', fontSize: 'small'}} />{t('homepage.subtask_2')}</ListItem>
                          <ListItem sx={{ ...listStyles, fontStyle: 'italic', pb: '0px', alignItems: 'baseline' }}><FiberManualRecordOutlinedIcon sx={{mr: '1rem', ml: '2rem', fontSize: 'small'}} />{t('homepage.subtask_3')}</ListItem>
                          <ListItem sx={listStyles}><ForwardOutlinedIcon sx={{mr: '1rem'}} />{t('homepage.task_2')}</ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grow>
                </Box>
              )}
            </InView>
          </Box>
          <Paper elevation='2' sx={{ ...paperStyles, width: { xs: '90%', sm: '80%', md: '70%' }, display: 'flex', alignItems: 'center' }}>
              <GppMaybeOutlinedIcon sx={{ fontSize: '3rem' }} />
              <Typography variant={isMobile ? 'body1' : 'subtitle1'} sx={{ fontWeight: 600, ml: '1rem' }}>{t('homepage.disclaimer')}</Typography>
          </Paper>
        </Box>
      </Fade>
    </div>
  )
}

export default Homepage;